import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Spinner,
  Input,
  Label,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  Upload,
  X,
  Save,
  FileJson,
  ChevronRight,
  ChevronLeft,
  Check,
  Image as ImageIcon,
} from "lucide-react";
import { useMenuItemsStore } from "../../../stores/Assets/menuItems";

const SAMPLE_JSON = {
  items: [
    {
      name: "Molten Cake",
      name_ar: "كعكة مولت",
      name_fr: "Fondant",
      description: "Molten Cake",
      description_ar: "كعكة مولت",
      description_fr: "Gateau chocolat",
      tags: ["Cake", "Molten"],
      price: 40,
    },
  ],
};
const BRAND_COLOR = "#c59253"; // Your requested theme color
const BRAND_LIGHT = "#f0d4b0"; // softer variant for backgrounds/hovers
const BRAND_DARK = "#a67840";
const BulkAddItemsModal = ({ isOpen, toggle, menuId, imageUrl, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [jsonInput, setJsonInput] = useState("");
  const [parseError, setParseError] = useState(null);
  const [validatedItems, setValidatedItems] = useState([]);
  const [validItems, setValidItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const { bulkAddItems, updateItem } = useMenuItemsStore();

  useEffect(() => {
    if (isOpen && menuId) {
      setStep(1);
      setJsonInput("");
      setParseError(null);
      setValidatedItems([]);
      setValidItems([]);
      setSummary(null);
      setItems([]);
    }
  }, [isOpen, menuId]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target?.result;
        setJsonInput(text ?? "");
        setParseError(null);
        validateAndParse(text);
      } catch {
        setParseError("Failed to read file");
      }
    };
    reader.readAsText(file, "UTF-8");
    e.target.value = "";
  };

  const validateAndParse = (text) => {
    if (!text?.trim()) {
      setParseError("Please enter or upload JSON content");
      setValidatedItems([]);
      setValidItems([]);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (!parsed.items || !Array.isArray(parsed.items)) {
        setParseError("JSON must contain an 'items' array");
        return;
      }
      setParseError(null);

      const validated = parsed.items.map((item, index) => {
        const errors = [];
        if (!item.name?.trim()) errors.push("Missing name");
        if (item.price === undefined) errors.push("Missing price");
        const price = Number(item.price);
        if (isNaN(price) || price < 0) errors.push("Invalid price");
        return { ...item, index: index + 1, errors };
      });

      setValidatedItems(validated);
      setValidItems(validated.filter((v) => v.errors.length === 0));
    } catch (err) {
      setParseError(`Invalid JSON: ${err.message}`);
      setValidatedItems([]);
      setValidItems([]);
    }
  };

  const handleJsonChange = (e) => {
    const val = e.target.value;
    setJsonInput(val);
    validateAndParse(val);
  };

  const loadSample = () => {
    const sampleStr = JSON.stringify(SAMPLE_JSON, null, 2);
    setJsonInput(sampleStr);
    validateAndParse(sampleStr);
  };

  // ──────────────────────────────────────────────
  //  ... (handleCreateItems, fetchItems, calculateSimilarity,
  //       handleImportImages, handleRemoveStaged,
  //       handleDragStart, handleDrop, handleSaveImages  remain mostly unchanged)
  // ──────────────────────────────────────────────
  const handleCreateItems = async () => {
    if (validItems.length === 0) {
      toast.warning("No valid items to add");
      return;
    }
    try {
      setLoading(true);
      const result = await bulkAddItems(
        menuId,
        validItems.map((i) => ({
          name: i.name?.trim(),
          name_ar: i.name_ar?.trim() || "",
          name_fr: i.name_fr?.trim() || "",
          description: i.description?.trim() || "",
          description_ar: i.description_ar?.trim() || "",
          description_fr: i.description_fr?.trim() || "",
          tags: Array.isArray(i.tags) ? i.tags : [],
          price:
            typeof i.price === "string" ? parseFloat(i.price) : Number(i.price),
        })),
      );
      setSummary(result);
      if (result.inserted > 0) {
        toast.success(`${result.inserted} items added`);
        setStep(2);
        fetchItems();
      } else {
        toast.warning(
          result.errors?.length
            ? "No items inserted. Check errors."
            : "No items to add",
        );
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to add items",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/menu/${menuId}/items?page=1&perPage=1000`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      if (!response.ok) throw new Error("Failed to fetch");
      const data = await response.json();
      setItems(
        (data.items || []).map((item) => ({
          ...item,
          item_id: item.menu_item_id,
          stagedImage: null,
          previewUrl: null,
        })),
      );
    } catch (err) {
      toast.error("Failed to load items");
    } finally {
      setLoading(false);
    }
  };

  const calculateSimilarity = (itemName, fileName) => {
    const a = itemName.toLowerCase().replace(/[^a-z0-9]/g, "");
    const b = fileName
      .toLowerCase()
      .replace(/\.[^/.]+$/, "")
      .replace(/[^a-z0-9]/g, "");
    let matches = 0;
    const temp = [...a];
    for (const c of b) {
      const i = temp.indexOf(c);
      if (i !== -1) {
        matches++;
        temp.splice(i, 1);
      }
    }
    return matches;
  };

  const handleImportImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const allMatches = [];
    files.forEach((file, fi) =>
      items.forEach((item, ii) => {
        const score = calculateSimilarity(item.title, file.name);
        if (score > 0)
          allMatches.push({ fileIndex: fi, itemIndex: ii, score, file, item });
      }),
    );
    allMatches.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return (
        Math.abs(
          a.file.name.replace(/[^a-z0-9]/gi, "").length -
            a.item.title.replace(/[^a-z0-9]/g, "").length,
        ) -
        Math.abs(
          b.file.name.replace(/[^a-z0-9]/gi, "").length -
            b.item.title.replace(/[^a-z0-9]/g, "").length,
        )
      );
    });
    const assignedF = new Set();
    const assignedI = new Set();
    const updated = items.map((i) => ({ ...i }));
    allMatches.forEach((m) => {
      if (!assignedF.has(m.fileIndex) && !assignedI.has(m.itemIndex)) {
        updated[m.itemIndex].stagedImage = m.file;
        updated[m.itemIndex].previewUrl = URL.createObjectURL(m.file);
        assignedF.add(m.fileIndex);
        assignedI.add(m.itemIndex);
      }
    });
    setItems(updated);
    toast.info(
      assignedI.size > 0
        ? `${assignedI.size} matched`
        : "No matches. Name images like item titles.",
    );
    e.target.value = "";
  };

  const handleRemoveStaged = (itemId) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.item_id === itemId && item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
          return { ...item, stagedImage: null, previewUrl: null };
        }
        return item;
      }),
    );
  };

  const handleDragStart = (e, sourceId) => {
    const src = items.find((i) => i.item_id === sourceId);
    if (!src?.stagedImage && !src?.images?.length) return;
    e.dataTransfer.setData("sourceItemId", sourceId);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = parseInt(e.dataTransfer.getData("sourceItemId"));
    if (sourceId === targetId) return;
    const si = items.findIndex((i) => i.item_id === sourceId);
    const ti = items.findIndex((i) => i.item_id === targetId);
    if (si === -1 || ti === -1) return;
    const updated = [...items];
    const tmpS = {
      stagedImage: updated[si].stagedImage,
      previewUrl: updated[si].previewUrl,
    };
    updated[si].stagedImage = updated[ti].stagedImage;
    updated[si].previewUrl = updated[ti].previewUrl;
    updated[ti].stagedImage = tmpS.stagedImage;
    updated[ti].previewUrl = tmpS.previewUrl;
    setItems(updated);
  };

  const handleSaveImages = async () => {
    const toUpdate = items.filter((i) => i.stagedImage);
    if (!toUpdate.length) {
      toast.warning("No images to save");
      return;
    }
    try {
      setSaving(true);
      for (const item of toUpdate) {
        const formData = new FormData();
        formData.append("newImages", item.stagedImage);
        await updateItem(menuId, item.item_id || item.menu_item_id, formData);
      }
      toast.success("Images saved!");
      onSuccess?.();
      toggle();
    } catch (err) {
      toast.error("Failed to save images");
    } finally {
      setSaving(false);
    }
  };

  const filteredItems = items.filter((i) =>
    i.title?.toLowerCase().includes(searchTerm?.toLowerCase() || ""),
  );

  const getItemImage = (item) =>
    item.previewUrl ||
    (item.images?.length ? `${imageUrl}${item.images[0]}` : null);

  const stagedCount = items.filter((i) => i.stagedImage).length;
  const invalidCount = validatedItems.length - validItems.length;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      scrollable
      centered
      className="bulk-import-modal"
    >
      <ModalHeader toggle={toggle} className="border-0 pb-2 pt-4 px-4">
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3 gap-sm-4">
          {/* Left: Title + Icon */}
          <div className="d-flex align-items-center gap-3 flex-shrink-0">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: 44,
                height: 44,
                background: `${BRAND_LIGHT}80`, // 50% opacity
              }}
            >
              <FileJson size={22} color={BRAND_COLOR} />
            </div>
            <div>
              <h5 className="mb-0 fw-semibold" style={{ color: BRAND_DARK }}>
                Bulk Import Items
              </h5>
              <small className="text-muted d-block">
                Add multiple menu items and attach images
              </small>
            </div>
          </div>

          {/* Right: Stepper – now wraps nicely */}
          <div
            className="d-flex align-items-center gap-3 gap-md-4 flex-wrap"
            style={{ minWidth: 0 }}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                style={{
                  width: 34,
                  height: 34,
                  background: step >= 1 ? BRAND_COLOR : "#e9ecef",
                  color: step >= 1 ? "white" : "#6c757d",
                }}
              >
                {step > 1 ? <Check size={18} /> : 1}
              </div>
              <span
                className="fw-medium d-none d-md-inline"
                style={{ color: step === 1 ? BRAND_COLOR : "#6c757d" }}
              >
                Upload JSON
              </span>
            </div>

            <div
              className="d-flex align-items-center flex-grow-1"
              style={{ minWidth: 60 }}
            >
              <div
                style={{
                  height: 3,
                  flex: 1,
                  background: step >= 2 ? BRAND_COLOR : "#e9ecef",
                  borderRadius: 2,
                }}
              />
            </div>

            <div className="d-flex align-items-center gap-2">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                style={{
                  width: 34,
                  height: 34,
                  background: step >= 2 ? BRAND_COLOR : "#e9ecef",
                  color: step >= 2 ? "white" : "#6c757d",
                }}
              >
                2
              </div>
              <span
                className="fw-medium d-none d-md-inline"
                style={{ color: step === 2 ? BRAND_COLOR : "#6c757d" }}
              >
                Attach Images
              </span>
            </div>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="px-4 pb-4 pt-2">
        {step === 1 ? (
          <Card className="border shadow-sm">
            <CardBody className="p-4 p-md-5">
              <Label className="fw-medium mb-3 d-block">JSON Content</Label>

              <div className="d-flex flex-column flex-md-row gap-4">
                <div className="flex-grow-1">
                  <Input
                    type="textarea"
                    value={jsonInput}
                    onChange={handleJsonChange}
                    placeholder='{"items": [{ "name": "...", "price": 25, ... }]}'
                    rows={15}
                    className="font-monospace bg-dark text-light border-0 shadow-sm"
                    style={{
                      fontSize: "0.95rem",
                      lineHeight: 1.6,
                      background: "#0f1117",
                      color: "#e2e8f0",
                      minHeight: 320,
                    }}
                  />
                  {parseError && (
                    <div className="text-danger mt-2 small fw-medium">
                      {parseError}
                    </div>
                  )}
                </div>

                <div className="d-flex flex-row flex-md-column gap-2 align-self-start align-self-md-stretch">
                  <Button
                    style={{
                      background: BRAND_COLOR,
                      borderColor: BRAND_COLOR,
                    }}
                    className="d-flex align-items-center justify-content-center gap-2 px-4 py-3"
                    onClick={() =>
                      document.getElementById("bulk-json-file").click()
                    }
                  >
                    <Upload size={18} />
                    Upload File
                  </Button>
                  <input
                    type="file"
                    id="bulk-json-file"
                    accept=".json"
                    hidden
                    onChange={handleFileSelect}
                  />

                  <Button
                    outline
                    style={{
                      borderColor: BRAND_COLOR,
                      color: BRAND_COLOR,
                    }}
                    className="d-flex align-items-center justify-content-center gap-2 px-4 py-3"
                    onClick={loadSample}
                  >
                    Load Sample
                  </Button>
                </div>
              </div>

              {validatedItems.length > 0 && (
                <div className="d-flex flex-column flex-sm-row justify-content-between small mt-4 pt-3 border-top gap-2">
                  <div>
                    <span style={{ color: "#2e7d32", fontWeight: 600 }}>
                      {validItems.length} valid
                    </span>
                    {invalidCount > 0 && (
                      <span className="text-danger ms-2">
                        • {invalidCount} invalid
                      </span>
                    )}
                  </div>
                  {invalidCount > 0 && (
                    <div className="text-danger" style={{ fontSize: "0.8rem" }}>
                      {validatedItems
                        .filter((v) => v.errors.length > 0)
                        .slice(0, 3)
                        .map(
                          (v) =>
                            `${v.name || `#${v.index}`}: ${v.errors.join(", ")}`,
                        )
                        .join(" • ")}
                      {invalidCount > 3 && ` + ${invalidCount - 3} more`}
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        ) : (
          <div className="d-flex flex-column gap-4 h-100">
            <div className="d-flex flex-column flex-sm-row align-items-stretch gap-3 flex-wrap">
              <Input
                type="text"
                placeholder="Search items by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ maxWidth: 400 }}
              />

              <Button
                style={{
                  background: BRAND_COLOR,
                  borderColor: BRAND_COLOR,
                }}
                className="d-flex align-items-center gap-2 px-4 py-2 ms-sm-auto"
                onClick={() =>
                  document.getElementById("bulk-image-upload").click()
                }
              >
                <Upload size={18} />
                Import Images
              </Button>
              <input
                type="file"
                id="bulk-image-upload"
                multiple
                accept="image/*"
                hidden
                onChange={handleImportImages}
              />
            </div>

            <div
              className="p-4 bg-light rounded border flex-grow-1 overflow-auto"
              style={{ background: "#fdfaf5" }} // very light warm tone
            >
              {filteredItems.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  {searchTerm
                    ? "No matching items"
                    : "Import images to begin matching"}
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-3">
                  {filteredItems.map((item) => {
                    const hasImage = !!getItemImage(item);
                    const isStaged = !!item.stagedImage;

                    return (
                      <div
                        key={item.item_id}
                        className={`position-relative rounded overflow-hidden shadow-sm transition-all ${
                          isStaged ? "ring" : ""
                        }`}
                        style={{
                          width: 148,
                          background: isStaged ? BRAND_LIGHT : "white",
                          border: isStaged
                            ? `2px solid ${BRAND_COLOR}`
                            : "1px solid #dee2e6",
                          transition: "all 0.15s ease",
                        }}
                        draggable={hasImage}
                        onDragStart={(e) => handleDragStart(e, item.item_id)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => handleDrop(e, item.item_id)}
                      >
                        <div
                          className="d-flex align-items-center justify-content-center bg-gradient-light"
                          style={{ height: 110 }}
                        >
                          {hasImage ? (
                            <img
                              src={getItemImage(item)}
                              alt={item.title}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <ImageIcon size={36} className="text-muted" />
                          )}
                        </div>
                        <div className="p-2 text-center">
                          <div className="small fw-medium text-truncate">
                            {item.title || "—"}
                          </div>
                        </div>

                        {isStaged && (
                          <>
                            <button
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 rounded-0 px-1 py-0"
                              onClick={() => handleRemoveStaged(item.item_id)}
                            >
                              <X size={14} />
                            </button>
                            <div
                              className="position-absolute bottom-0 start-0 end-0 text-white text-center py-1"
                              style={{
                                fontSize: 11,
                                background: BRAND_COLOR,
                              }}
                            >
                              New Image
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter
        className="px-4 py-3 border-0"
        style={{ background: "#fdfaf5" }}
      >
        {step === 1 ? (
          <>
            <Button color="light" onClick={toggle}>
              Cancel
            </Button>
            <Button
              style={{
                background: BRAND_COLOR,
                borderColor: BRAND_COLOR,
              }}
              disabled={loading || validItems.length === 0}
              onClick={handleCreateItems}
              className="d-flex align-items-center gap-2 px-4"
            >
              {loading ? (
                <>
                  <Spinner size="sm" /> Creating…
                </>
              ) : (
                <>
                  Add {validItems.length} Items
                  <ChevronRight size={18} />
                </>
              )}
            </Button>
          </>
        ) : (
          <>
            <Button color="light" onClick={() => setStep(1)} disabled={saving}>
              <ChevronLeft size={18} className="me-1" />
              Back
            </Button>
            <Button color="light" onClick={toggle} disabled={saving}>
              {stagedCount > 0 ? "Skip & Finish" : "Done"}
            </Button>
            <Button
              style={{
                background: BRAND_COLOR,
                borderColor: BRAND_COLOR,
              }}
              disabled={saving || stagedCount === 0}
              onClick={handleSaveImages}
              className="d-flex align-items-center gap-2 px-4"
            >
              {saving ? (
                <>
                  <Spinner size="sm" /> Saving…
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save {stagedCount} Image{stagedCount !== 1 && "s"}
                </>
              )}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};
export default BulkAddItemsModal;
